import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import {
  DtoV1PropertyTaxSaveEscrowDetailsRequestDto,
  DtoV1PropertyTaxSavePropertyTaxDataRequestDto,
} from '~/__generated__/api';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import { TOAST_ERROR_DESCRIPTION_UNDERWRITER } from '~/common/constants/language.json';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import { HomeActions } from '~/pages/Home/redux';

export const useTaxBreakdownData = () => {
  const { obfuscatedLoanIdentifier } = useUrl();
  const showSnackbar = useSnackbar();
  const taxesResponse = useQuery({
    queryKey: ['tax-breakdown'],
    queryFn: async () => {
      const response = await typedApiClient.v1.propertyTaxesTaxBreakdownDetail(
        obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
        obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
      );
      return {
        ...response.data,
        isTaxVerifiedPreviously: response.data.isTaxVerified,
        isTaxVerifiedForEarlyCdPreviously:
          response.data.isTaxVerifiedForEarlyCD,
      };
    },
  });

  const { mutateAsync: saveTaxBreakdown } = useMutation({
    mutationKey: ['tax-breakdown'],
    mutationFn: async (data: DtoV1PropertyTaxSavePropertyTaxDataRequestDto) => {
      await typedApiClient.v1.propertyTaxesTaxBreakdown(
        obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
        obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
        data,
      );
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: TOAST_ERROR_DESCRIPTION_UNDERWRITER,
        duration: 6000,
      });
    },
  });

  return {
    ...taxesResponse,
    saveTaxBreakdown,
    obfuscatedLoanIdentifier,
  };
};

export const useIndividualTaxData = (taxId: string) => {
  const { obfuscatedLoanIdentifier } = useUrl();
  const showSnackbar = useSnackbar();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutate: saveTaxes } = useMutation({
    mutationKey: ['taxes', taxId],
    mutationFn: async (
      data: Omit<
        DtoV1PropertyTaxSaveEscrowDetailsRequestDto,
        'obfuscatedLoanIdentifier'
      >,
    ) => {
      await typedApiClient.v1.propertyTaxesEscrowDetails({
        ...data,
        obfuscatedLoanIdentifier,
      });
    },
    onError: () => {
      showSnackbar({
        severity: 'error',
        message: TOAST_ERROR_DESCRIPTION_UNDERWRITER,
        duration: 6000,
      });
      dispatch(HomeActions.setShouldGetNextAlert(false));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['taxes', taxId]);
    },
  });

  const taxesResponse = useQuery({
    queryKey: ['taxes', taxId],
    enabled: Boolean(taxId),
    queryFn: async () => {
      const response = await typedApiClient.v1.propertyTaxesEscrowDetailsDetail(
        obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
        obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
        taxId,
      );
      return response.data;
    },
  });

  return {
    ...taxesResponse,
    saveTaxes,
    obfuscatedLoanIdentifier,
  };
};
